import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { UserContext } from '../../context/UserContext';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { makeStyles } from '@mui/styles';
import { PromptAlert } from 'features/ux/RouterPrompt';
import SweetAlert from 'react-bootstrap-sweetalert';
let pendingSwitchClient = null;

import {
  ThemedPrimaryBtn,
  ThemedSecondaryBtn,
} from '@themes/defaultThemeProps';
import { selectViewIsDirty, setViewIsDirty } from 'features/ux/uxSlice';
import {
  selectNTadministrator,
  selectSessionStatus,
  setImpersonatedClient,
  setImpersonatedTenant,
  setImpersonationMode,
  setImpersonationSessionData,
  setToken,
} from '../../features/session/sessionSlice';

import {
  useGetClientsQuery,
  useLazyGetClientTenantsQuery,
} from 'api/client.api';
import { useLazyGetLocationsQuery } from 'api/location.api';
import {
  useLazyGetParameterListsQuery,
  useLazyGetTemplateSetsQuery,
} from 'api/parameter.api';
import { useLazyGetReferenceListsQuery } from 'api/reference-list.api';
import { useLazyGetRolesQuery } from 'api/role.api';
import {
  useLazyGetSessionQuery,
  useRequestImpersonationSessionMutation,
} from 'api/session.api';
import {
  tenantApi,
  useLazyGetTenantQuery,
  useLazyGetTenantStatsQuery,
} from 'api/tenant.api';
import commonStyle from 'assets/jss/material-dashboard-pro-react/commonStyle';
import { setClientTenants } from 'features/client/clientSlice';
import { retrieveNotifications } from 'features/notification/notificationSlice';
import dashboardDates from 'views/Dashboard/dashboardDates.const';

const SelectClient = (props) => {
  const useStyles = makeStyles((theme) => ({
    ...commonStyle,
    margin: {
      margin: theme.spacing(1),
    },
  }));

  const classes = useStyles();
  const dispatch = useDispatch();

  const viewIsDirty = useSelector(selectViewIsDirty);
  const sessionStatus = useSelector(selectSessionStatus);
  const ntAdmin = useSelector(selectNTadministrator);
  const [triggerGetSession] = useLazyGetSessionQuery();
  const [triggerGetTenant] = useLazyGetTenantQuery();
  const [triggerGetTenantStats] = useLazyGetTenantStatsQuery();
  const [impersonateSession] = useRequestImpersonationSessionMutation();
  const [triggerGetParameterLists] = useLazyGetParameterListsQuery();
  const [triggerGetTemplateSets] = useLazyGetTemplateSetsQuery();
  const [triggerGetRoles] = useLazyGetRolesQuery();
  const [triggerGetLocations] = useLazyGetLocationsQuery();
  const [triggerGetReferenceLists] = useLazyGetReferenceListsQuery();
  const [
    triggerGetClientTenants,
    { data: clientTenants, isFetching, isSuccess },
  ] = useLazyGetClientTenantsQuery();
  const { data: clients } = useGetClientsQuery(undefined, {
    skip: sessionStatus !== 'active' || !ntAdmin,
  });
  const getTenantRetrievalStatus = () => {
    if (isFetching) return 'pending';
    if (isSuccess) return 'success';
    return 'failure';
  };
  const tenantRetrievalStatus = getTenantRetrievalStatus();

  const [userContext, setUserContext] = useContext(UserContext);
  const history = useHistory();

  const [client, setClient] = useState(0);
  const [switchingClient, setSwitchingClient] = useState(false);
  const [processingSwitch, setProcessingSwitch] = useState(false);
  const [showDirtySwitchAlert, setShowDirtySwitchAlert] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const activeClients = clients?.filter((client) => client.status === 'active');

  const clientList = () => {
    const list = activeClients?.map((entry, index) => (
      <MenuItem key={entry._id} value={index}>
        {entry.name}
      </MenuItem>
    ));
    return list;
  };

  const initializeImpersonationSession = async (token) => {
    const sessionData = await triggerGetSession({ token }).unwrap();

    dispatch(setToken(sessionData?.token));
    const period = dashboardDates[0].replace(/\s+/g, '').toLowerCase();
    const tenantData = await triggerGetTenant().unwrap();
    dispatch(setImpersonatedTenant(tenantData));
    await dispatch(tenantApi.util.invalidateTags(['Tenant']));

    // Run the remaining operations in parallel for better performance
    await Promise.all([
      triggerGetTenantStats({ period }).unwrap(),
      triggerGetRoles().unwrap(),
      triggerGetReferenceLists().unwrap(),
      triggerGetLocations({ limit: 20 }).unwrap(),
      triggerGetParameterLists().unwrap(),
      triggerGetTemplateSets().unwrap(),
      dispatch(retrieveNotifications()),
    ]);
    dispatch(setImpersonationSessionData(sessionData));
  };

  useEffect(() => {
    if (
      !processingSwitch &&
      switchingClient &&
      tenantRetrievalStatus === 'success'
    ) {
      setProcessingSwitch(true);
      setIsSwitching(true);
      if (clientTenants.length > 0) {
        dispatch(setImpersonatedTenant(clientTenants[0])); // default to the first tenant in the list for the selected client
        // get new token for the newly selected tenant
        const body = {
          client_id: activeClients[client]._id,
          tenant_id: clientTenants[0].tenant_id,
        };
        impersonateSession({
          clientId: body.client_id,
          tenantId: body.tenant_id,
        })
          .unwrap()
          .then(async (response) => {
            try {
              if (!response.success) {
                setUserContext((oldValues) => ({ ...oldValues, token: null }));
                return;
              }

              setUserContext((oldValues) => ({
                ...oldValues,
                token: response.token,
              }));
              await initializeImpersonationSession(response.token);
              dispatch(setImpersonationMode(true));
              props.switchClient(client);
              setSwitchingClient(false);
              setProcessingSwitch(false);
            } finally {
              setIsSwitching(false);
            }
          })
          .catch((error) => {
            console.log(error);
            setSwitchingClient(false);
            setProcessingSwitch(false);
            setIsSwitching(false);
          });
      }
    }
  }, [switchingClient, tenantRetrievalStatus, clientTenants, client, props]);

  const switchClient = async (client) => {
    if (viewIsDirty) {
      pendingSwitchClient = client;
      setShowDirtySwitchAlert(true);
      props.setClientSelectOpen(false);
      return;
    }

    dispatch(setImpersonatedClient(client));
    const fetchedClientTenants = await triggerGetClientTenants(
      client._id
    ).unwrap();
    dispatch(setClientTenants(fetchedClientTenants));
    setSwitchingClient(true);
    pendingSwitchClient = null;
  };

  useEffect(() => {
    if (pendingSwitchClient !== null) {
      switchClient(pendingSwitchClient);
    }
  }, [viewIsDirty]);

  return (
    <>
      {isSwitching && (
        <SweetAlert
          style={{ display: 'block', marginTop: '-100px' }}
          title={<span style={{ color: '#3c4858' }}>Switching Client!</span>}
          showConfirm={false}
          onConfirm={() => {}}
        >
          <span style={{ color: '#3c4858' }}>
            Please wait while we load the new client and environment...
          </span>
        </SweetAlert>
      )}
      {showDirtySwitchAlert && (
        <PromptAlert
          onConfirm={() => {
            setShowDirtySwitchAlert(false);
            dispatch(setViewIsDirty(false));
          }}
          onCancel={() => {
            setShowDirtySwitchAlert(false);
            pendingSwitchClient = null;
          }}
        />
      )}
      <Dialog
        open={props.clientSelectOpen && !isSwitching}
        onClose={() => props.setClientSelectOpen(false)}
        aria-labelledby='form-dialog-title'
      >
        <DialogTitle id='form-dialog-title'>Select Client</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select a client organization to use during this session.
          </DialogContentText>
          <FormControl
            className={classes.outlineSelect}
            variant='outlined'
            style={{ minWidth: 200 }}
          >
            <InputLabel
              id='default-client-label'
              className={classes.inputLabel}
            >
              Client
            </InputLabel>
            <Select
              variant='standard'
              className={classes.outlineSelect}
              labelId='default-client-label'
              id='client'
              value={client}
              onChange={(event) => setClient(event.target.value)}
              label='Client'
            >
              {clientList()}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <ThemedSecondaryBtn
            color='secondary'
            onClick={() => props.setClientSelectOpen(false)}
          >
            Cancel
          </ThemedSecondaryBtn>
          <ThemedPrimaryBtn
            color='primary'
            onClick={() => switchClient(activeClients[client])}
          >
            Switch
          </ThemedPrimaryBtn>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectClient;
